import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
// react-easy-crop ships its base layout styles in a separate stylesheet that
// neither its ESM nor CJS entry imports. Without it the cropped image has no
// sizing/positioning and collapses to an empty (dark) circle — so load it here.
import 'react-easy-crop/react-easy-crop.css';
import { X, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { backdropVariants, modalVariants } from '../../utils/chat/motion';
import { cropImageToBlob, type PixelCrop } from '../../utils/chat/image';

interface Props {
  /** The image the user picked; previewed locally (never uploaded as-is). */
  file: File;
  /** Crop + hand back the resulting JPEG blob. Throw to surface an error. */
  onSave: (blob: Blob) => Promise<void>;
  onCancel: () => void;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.2;

// Fractal-noise texture for the editing stage — gives the dark backdrop a
// tactile, film-grain quality instead of a flat fill.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// Scoped CSS: a premium range slider, an accent-lit crop ring, and the
// keyframes for the saving shimmer + spinner. Injected into <head> once (see
// effect below) so it never has to live in the global stylesheet, which
// doesn't hot-reload in this project.
const STYLE_ID = 'gtacm-style';
const STYLE = `
.gtacm-range {
  -webkit-appearance: none; appearance: none;
  height: 6px; border-radius: 999px; outline: none;
}
.gtacm-range::-webkit-slider-runnable-track { height: 6px; border-radius: 999px; background: transparent; }
.gtacm-range::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  width: 18px; height: 18px; margin-top: -6px; border-radius: 50%;
  background: #fff; border: 2px solid var(--gt-accent, #1f4030);
  box-shadow: 0 2px 6px rgba(0,0,0,.28), 0 0 0 4px rgba(110,231,183,.16);
  transition: transform .16s cubic-bezier(.34,1.56,.64,1), box-shadow .16s ease;
  cursor: grab;
}
.gtacm-range:hover::-webkit-slider-thumb { transform: scale(1.14); box-shadow: 0 3px 10px rgba(0,0,0,.32), 0 0 0 7px rgba(110,231,183,.22); }
.gtacm-range:active::-webkit-slider-thumb { transform: scale(1.04); cursor: grabbing; }
.gtacm-range::-moz-range-track { height: 6px; border-radius: 999px; background: transparent; }
.gtacm-range::-moz-range-progress { height: 6px; border-radius: 999px; background: var(--gt-accent, #1f4030); }
.gtacm-range::-moz-range-thumb {
  width: 16px; height: 16px; border-radius: 50%;
  background: #fff; border: 2px solid var(--gt-accent, #1f4030);
  box-shadow: 0 2px 6px rgba(0,0,0,.28), 0 0 0 4px rgba(110,231,183,.16);
  cursor: grab;
}
.gtacm-range:disabled { opacity: .45; cursor: default; }

.gtacm-stage .reactEasyCrop_CropArea {
  border: 2px solid rgba(255,255,255,.94) !important;
  box-shadow:
    0 0 0 9999em rgba(7,12,10,.66),
    0 0 0 1px rgba(110,231,183,.45),
    0 0 34px 6px rgba(110,231,183,.22) !important;
  transition: box-shadow .3s ease;
}

@keyframes gtacm-spin { to { transform: rotate(360deg); } }
@keyframes gtacm-breathe { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: .9; transform: scale(1.06); } }
@keyframes gtacm-shimmer { 0% { transform: translateX(-120%); } 100% { transform: translateX(220%); } }
.gtacm-spin { animation: gtacm-spin .8s linear infinite; }
`;

const content: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.34, ease: [0.33, 1, 0.68, 1] } },
};

/**
 * Full-screen modal that previews a freshly-picked avatar and lets the user
 * pan/zoom inside a circular frame before uploading. Portaled to <body> so the
 * Radix profile popover can't clip it. The preview uses a local object URL, so
 * the user always sees their photo regardless of network/remote state.
 */
export default function AvatarCropModal({ file, onSave, onCancel }: Props) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const areaPixelsRef = useRef<PixelCrop | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inject the scoped stylesheet once. Idempotent (keyed by id) so React 18's
  // StrictMode double-mount can't add it twice.
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLE;
    document.head.appendChild(el);
  }, []);

  // Create the preview object URL INSIDE the effect so each mount owns and
  // revokes its own URL. Doing this in useMemo + a separate revoke effect is
  // unsafe under React 18 StrictMode: the dev double-invoke (setup → cleanup →
  // setup) revokes the URL the committed <img> still references, leaving the
  // cropper blank.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Close on Escape (unless mid-upload).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    areaPixelsRef.current = areaPixels;
  }, []);

  const stepZoom = (delta: number) => {
    if (busy) return;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z + delta).toFixed(2))));
  };

  const handleSave = async () => {
    const area = areaPixelsRef.current;
    if (!area) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await cropImageToBlob(file, area, 512);
      await onSave(blob);
      // onSave is responsible for closing the modal on success.
    } catch {
      setError("Couldn't update photo. Please try again.");
      setBusy(false);
    }
  };

  const zoomPct = Math.round(zoom * 100);
  const fillPct = ((zoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100;
  const trackBg = `linear-gradient(to right, var(--gt-accent, #1f4030) 0%, var(--gt-accent, #1f4030) ${fillPct}%, rgba(127,127,127,.22) ${fillPct}%, rgba(127,127,127,.22) 100%)`;

  const stepBtn = {
    width: 30, height: 30, flexShrink: 0,
    display: 'grid', placeItems: 'center',
    borderRadius: 9, border: '1px solid var(--gt-border, rgba(0,0,0,.1))',
    background: 'var(--gt-surface-1, #fff)',
    color: 'var(--gt-text-secondary, #4a4a4a)',
    cursor: busy ? 'default' : 'pointer',
  } as const;

  return createPortal(
    <AnimatePresence>
      <motion.div
        variants={backdropVariants}
        initial="hidden" animate="visible" exit="exit"
        onClick={() => { if (!busy) onCancel(); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'radial-gradient(120% 120% at 50% 0%, rgba(15,45,31,.42), rgba(7,12,10,.66))',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
      >
        <motion.div
          variants={modalVariants}
          initial="hidden" animate="visible" exit="exit"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 384, maxWidth: '100%',
            background: 'var(--gt-surface-0, #fff)',
            border: '1px solid var(--gt-border, rgba(0,0,0,.08))',
            borderRadius: 'var(--gt-radius-modal, 22px)',
            boxShadow: 'var(--gt-elev-5, 0 28px 64px -14px rgba(20,20,18,.16)), 0 0 0 1px rgba(110,231,183,.06)',
            overflow: 'hidden',
            fontFamily: 'var(--gt-font, system-ui, sans-serif)',
            position: 'relative',
          }}
        >
          {/* Top accent hairline — ties the modal to the brand's aurora gradient */}
          <div
            aria-hidden
            style={{
              height: 3,
              // accent → accent-strong stays visible in BOTH themes (dark-green
              // hairline on light, mint hairline on dark). The --gt-grad-* tokens
              // are both dark greens in dark mode and would disappear there.
              background: 'linear-gradient(90deg, var(--gt-accent, #1f4030), var(--gt-accent-strong, #0f2d1f))',
            }}
          />

          <motion.div variants={content} initial="hidden" animate="visible">
            {/* Header */}
            <motion.div
              variants={item}
              style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                padding: '15px 16px 11px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase',
                    color: 'var(--gt-accent, #1f4030)',
                  }}
                >
                  Profile photo
                </span>
                <span
                  style={{
                    fontSize: 17, fontWeight: 700, lineHeight: 1.1,
                    fontFamily: 'var(--gt-font-display, system-ui)',
                    color: 'var(--gt-text-primary, #111827)',
                  }}
                >
                  Adjust photo
                </span>
              </div>
              <motion.button
                type="button"
                onClick={onCancel}
                disabled={busy}
                aria-label="Close"
                whileHover={busy ? undefined : { backgroundColor: 'var(--gt-surface-2, #f5f5f5)', rotate: 90 }}
                whileTap={busy ? undefined : { scale: 0.9 }}
                transition={{ duration: 0.18 }}
                style={{
                  width: 30, height: 30, display: 'grid', placeItems: 'center',
                  background: 'transparent', border: 0, borderRadius: 9,
                  cursor: busy ? 'default' : 'pointer',
                  color: 'var(--gt-text-muted, #6b7280)',
                }}
              >
                <X size={16} />
              </motion.button>
            </motion.div>

            {/* Cropper stage */}
            <motion.div variants={item} style={{ padding: '0 16px' }}>
              <div
                className="gtacm-stage"
                style={{
                  position: 'relative', width: '100%', height: 300,
                  borderRadius: 16, overflow: 'hidden',
                  background: 'radial-gradient(110% 90% at 50% 30%, #15231b 0%, #0c130f 55%, #060a08 100%)',
                  border: '1px solid var(--gt-border, rgba(0,0,0,.08))',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,.4)',
                }}
              >
                {/* Soft accent glow that breathes behind the crop circle */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', inset: 0, zIndex: 0,
                    background: 'radial-gradient(closest-side, rgba(110,231,183,.16), transparent 70%)',
                    animation: 'gtacm-breathe 5.5s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                />
                {imageUrl && (
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    restrictPosition
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                )}
                {/* Grain overlay for tactile depth */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
                    backgroundImage: GRAIN, backgroundSize: '160px 160px',
                    opacity: 0.06, mixBlendMode: 'soft-light',
                  }}
                />
                {/* Interaction hint */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)',
                    zIndex: 3, pointerEvents: 'none', whiteSpace: 'nowrap',
                    padding: '5px 11px', borderRadius: 999,
                    fontSize: 11, fontWeight: 600, letterSpacing: '.01em',
                    color: 'rgba(255,255,255,.86)',
                    background: 'rgba(12,18,14,.5)',
                    border: '1px solid rgba(255,255,255,.12)',
                    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                  }}
                >
                  Drag to reposition · scroll to zoom
                </div>
              </div>
            </motion.div>

            {/* Zoom control */}
            <motion.div
              variants={item}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '16px 18px 8px',
              }}
            >
              <motion.button
                type="button"
                onClick={() => stepZoom(-ZOOM_STEP)}
                disabled={busy || zoom <= ZOOM_MIN}
                aria-label="Zoom out"
                whileHover={busy ? undefined : { y: -1, borderColor: 'var(--gt-accent, #1f4030)', color: 'var(--gt-accent, #1f4030)' }}
                whileTap={busy ? undefined : { scale: 0.92 }}
                style={{ ...stepBtn, opacity: zoom <= ZOOM_MIN ? 0.45 : 1 }}
              >
                <ZoomOut size={15} />
              </motion.button>
              <input
                className="gtacm-range"
                type="range"
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.01}
                value={zoom}
                aria-label="Zoom"
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={busy}
                style={{ flex: 1, background: trackBg, cursor: busy ? 'default' : 'pointer' }}
              />
              <motion.button
                type="button"
                onClick={() => stepZoom(ZOOM_STEP)}
                disabled={busy || zoom >= ZOOM_MAX}
                aria-label="Zoom in"
                whileHover={busy ? undefined : { y: -1, borderColor: 'var(--gt-accent, #1f4030)', color: 'var(--gt-accent, #1f4030)' }}
                whileTap={busy ? undefined : { scale: 0.92 }}
                style={{ ...stepBtn, opacity: zoom >= ZOOM_MAX ? 0.45 : 1 }}
              >
                <ZoomIn size={15} />
              </motion.button>
              <div
                style={{
                  flexShrink: 0, minWidth: 46, textAlign: 'center',
                  padding: '5px 8px', borderRadius: 8,
                  fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '.01em',
                  color: 'var(--gt-accent-strong, #0f2d1f)',
                  background: 'var(--gt-primary-container, #e8efe9)',
                }}
              >
                {zoomPct}%
              </div>
            </motion.div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    margin: '2px 18px 0', padding: '8px 12px', borderRadius: 10,
                    fontSize: 12, fontWeight: 600, textAlign: 'center',
                    color: '#dc2626',
                    background: 'rgba(220,38,38,.08)',
                    border: '1px solid rgba(220,38,38,.18)',
                  }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer actions */}
            <motion.div
              variants={item}
              style={{ display: 'flex', gap: 10, padding: '14px 16px 18px' }}
            >
              <motion.button
                type="button"
                onClick={onCancel}
                disabled={busy}
                whileHover={busy ? undefined : { backgroundColor: 'var(--gt-surface-2, #f5f5f5)' }}
                whileTap={busy ? undefined : { scale: 0.97 }}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 12,
                  border: '1px solid var(--gt-border, rgba(0,0,0,.14))',
                  background: 'transparent',
                  color: 'var(--gt-text-secondary, #4a4a4a)',
                  fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="button"
                onClick={handleSave}
                disabled={busy}
                whileHover={busy ? undefined : { y: -1.5, boxShadow: '0 10px 24px -6px rgba(15,45,31,.45), 0 0 0 1px rgba(110,231,183,.3)' }}
                whileTap={busy ? undefined : { scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 26 }}
                style={{
                  flex: 1.35, padding: '11px 0', borderRadius: 12, border: 0,
                  position: 'relative', overflow: 'hidden',
                  // accent pairs with --gt-on-primary in every theme: dark-green
                  // + white (light) / mint + near-black (dark) — always legible.
                  background: 'linear-gradient(135deg, var(--gt-accent, #1f4030), var(--gt-accent-strong, #0f2d1f))',
                  color: 'var(--gt-on-primary, #fff)',
                  fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
                  cursor: busy ? 'default' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  boxShadow: '0 6px 16px -6px rgba(15,45,31,.4)',
                }}
              >
                {busy ? (
                  <>
                    <Loader2 size={15} className="gtacm-spin" />
                    Saving…
                    {/* Shimmer sweep while uploading */}
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute', top: 0, bottom: 0, left: 0, width: '45%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.28), transparent)',
                        animation: 'gtacm-shimmer 1.2s ease-in-out infinite',
                      }}
                    />
                  </>
                ) : (
                  'Save photo'
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
